<template>
  <component
    :is="sourceDeleting ? 'BittsTooltip' : 'div'"
    v-bind="
      sourceDeleting
        ? { class: 'cursor-not-allowed', placement: 'bottomLeft' }
        : null
    "
  >
    <template v-if="sourceDeleting" #title>
      This data source has been deleted, this Population will be deleted shortly
    </template>
    <div
      class="c-population-item"
      :class="{
        'c-population-item--new': !isEmptyPopulation,
      }"
      role="presentation"
      @click="handlePopulationClick"
    >
      <div class="flex items-center flex-1">
        <div v-if="sourceDeleting" class="flex items-center">
          <FontAwesomeIcon
            :icon="['fak', 'warning']"
            class="text-warning-accent text-16 mr-4"
          />
        </div>
        <div
          v-if="isOpenData && !isEmptyPopulation"
          class="c-population-item__open-data-icon"
        >
          <FontAwesomeIcon
            :icon="['fak', 'managed-offline-partner']"
            class="text-info-accent text-20"
          />
        </div>
        <BittsAvatar
          v-else-if="!isEmptyPopulation && !isOfflinePartner"
          size="medium"
          shape="square"
          :show-border="showBorder"
          :custom-svg="
            population.integration_type
              ? `${population.integration_type}Icon`
              : ''
          "
          class="c-population-item__integration-avatar"
        />
        <BittsTooltip placement="bottomLeft">
          <button
            type="button"
            class="c-population-item__name"
            data-testid="population-name"
            @click.stop="handlePopulationClick"
          >
            {{ population.name }}
          </button>
          <template #title>
            <p v-if="population.description">{{ population.description }}</p>
            <p v-else-if="noDescriptionText"> {{ noDescriptionText }} </p>
            <br
              v-if="
                (population.description || noDescriptionText) && processedAtText
              "
            />
            <span v-if="!isUpdating && processedAtText">
              Last updated: <strong>{{ processedAtText }}</strong>
            </span>
            <span v-else-if="isUpdating"> Updating... </span>
          </template>
        </BittsTooltip>
        <component :is="divOrTooltip" placement="bottomLeft">
          <BittsTag
            v-if="showTag && (!population.standard_type || population?.tag)"
            size="x-small"
            :color="population.segment ? 'neutral' : 'info'"
            :text="population?.tag || capitalizedSegment"
          />
          <template #title>
            <p class="font-bold">Population type</p>
            <span>
              This shows how this population is classified in Crossbeam to
              ensure it appears correctly in mapping and reporting. You can
              update the type when editing the population
            </span>
          </template>
        </component>
        <BittsTag
          v-if="isOpenData"
          size="x-small"
          color="info"
          text="Open Data"
        />
      </div>
      <div
        class="flex flex-col items-start w-[220px] shrink-0"
        v-if="population?.dataShare && !isDisabled && !isOfflinePartner"
      >
        <BittsTooltip v-if="canManagePopulations || canManageSharing">
          <button
            type="button"
            class="flex justify-start items-start cursor-pointer"
            data-testid="edit-sharing"
            @click.stop="onEditSharing"
          >
            <SharingSettingsVisibilitySelector
              :initial-visibility="population.dataShare?.visibility"
              read-only
            />
          </button>
          <template #title>Edit Sharing setting</template>
        </BittsTooltip>
      </div>
      <div class="flex items-center w-[195px] shrink-0">
        <PermissionIndicator
          v-if="population?.tag"
          resource="populations"
          class="ml-auto"
        >
          <BittsButton
            text="Create"
            size="small"
            :disabled="
              isOfflinePartner
                ? !canManageOfflinePartners
                : !canManagePopulations
            "
            :left-icon="['fak', 'add']"
            @click.stop="emit('create-population')"
          />
        </PermissionIndicator>
        <div v-else class="flex items-center w-full gap-12">
          <BittsTag
            color="secondary"
            class="self-start"
            :text="pluralizedRecords"
          />
          <div class="flex items-center ml-auto">
            <!-- Offline partner: show delete button instead of edit/overflow menu -->
            <BittsButton
              v-if="isOfflinePartner && !sourceDeleting"
              text="Delete"
              type="danger"
              variant="ghost"
              size="small"
              :disabled="!canManageOfflinePartners"
              @click.stop="handleOfflineDeletePopulation"
            />
            <!-- Regular partner: show edit button and overflow menu -->
            <template v-else-if="!isOfflinePartner && !isOpenData">
              <BittsTooltip>
                <BittsButton
                  v-if="canManagePopulations && !sourceDeleting"
                  :center-icon="['fak', 'edit']"
                  size="x-small"
                  type="neutral"
                  variant="outline"
                  class="mr-4"
                  aria-label="Edit population"
                  @click.stop="emit('pop-clicked')"
                />
                <template #title> Edit Population </template>
              </BittsTooltip>

              <PopulationOverflowMenu
                v-if="!sourceDeleting"
                :at-or-above-cap="atOrAboveCap"
                :can-manage-populations="canManagePopulations"
                :is-standard="!!population.standard_type"
                :population="population"
                :record-number="recordNumber"
                :sharing-link="sharingLink"
                :standard-populations="standardPopulations"
                :class="{ 'disabled cursor-not-allowed': isDisabled }"
                class="c-population-item__overflow-menu"
                @click.stop=""
                @convert-to-custom="modals.convertToCustom = true"
                @convert-to-standard="modals.convertToStandard = true"
                @delete-population="handleDeletePopulation"
                @duplicate-population="duplicatePopulation"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </component>
  <DeletePopulationModal
    v-if="showDeleteModal"
    :visible="showDeleteModal"
    :population-id="population.id"
    @close="showDeleteModal = false"
  />
  <ConvertToStandardModal
    v-if="modals.convertToStandard && standardPopulations.length < 3"
    :has-population-refresh="true"
    :population="population"
    :standard-populations="standardPopulations"
    :visible="modals.convertToStandard"
    @closed="modals.convertToStandard = false"
    @population-converted="refreshPopulations"
  />
  <ConvertToCustomModal
    v-if="modals.convertToCustom && population"
    :standard-population="population"
    :has-population-refresh="true"
    :population="population"
    :visible="modals.convertToCustom"
    @closed="modals.convertToCustom = false"
    @population-converted="refreshPopulations"
  />
</template>
<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { intComma } from 'humanize-plus';
import { capitalize } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConvertToCustomModal from '@/components/populations/ConvertToCustomModal.vue';
import ConvertToStandardModal from '@/components/populations/ConvertToStandardModal.vue';
import DeletePopulationModal from '@/components/populations/DeletePopulationModal.vue';
import PopulationOverflowMenu from '@/components/populations/PopulationOverflowMenu.vue';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { SharingType } from '@/constants/data_shares';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SOURCE_STATUS_MAP,
} from '@/constants/data_sources';
import {
  useFlashesStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import { SharedField } from '@/types/data_shares';
import {
  FilterGroup,
  FilterPart,
  Population,
  PopulationStats,
  isOpenDataPopulation,
} from '@/types/populations';
import { populationProcessedAtText } from '@/utils';

import PermissionIndicator from '../PermissionIndicator.vue';

const {
  population,
  atOrAboveCap,
  isOfflinePartner,
  offlinePartnerUuid,
  showTag = true,
} = defineProps<{
  population: Population & {
    meta?: PopulationStats;
    isEmpty?: boolean;
    tag?: string;
    dataShare?: {
      visibility: SharingType;
      source_field_ids?: number[];
      sourceFields?: SharedField[];
    };
  };
  atOrAboveCap: boolean;
  isOfflinePartner?: boolean;
  offlinePartnerUuid?: string;
  showTag?: boolean;
}>();

const emit = defineEmits<{
  'pop-clicked': [];
  'view-population': [];
  'create-population': [];
  'edit-population': [];
}>();

const sourcesStore = useSourcesStore();
const populationsStore = usePopulationsStore();
const flashesStore = useFlashesStore();
const { hasScope } = useAuth();
const router = useRouter();
const route = useRoute();

const canManageOfflinePartners = computed(() =>
  hasScope('write:offline-partners'),
);
const canManageSharing = computed(() => hasScope('write:sharing'));

const modals = reactive({
  convertToStandard: false,
  convertToCustom: false,
});

const showDeleteModal = ref(false);
const processedAtText = computed(() =>
  populationProcessedAtText(population?.meta?.updated_at || ''),
);

const noDescriptionText = computed(() => {
  if (!population.standard_type) return null;
  const DESCRIPTION_MAP = {
    customers: 'This should be a list of your current customers',
    open_opportunities: 'This should be a list of accounts in your pipeline',
    prospects: 'This should be accounts not in your sales cycle',
  };
  return DESCRIPTION_MAP[population.standard_type];
});

const capitalizedSegment = computed(() => {
  if (!population?.segment) return '';
  return population.segment
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
});

const canManagePopulations = computed(() => hasScope('write:populations'));

const sourceDeleting = computed(() => {
  const source = sourcesStore.getSourceById(population.source_id);
  const deletingStatuses = SOURCE_STATUS_MAP.deleting as readonly string[];
  return !!(source?.status && deletingStatuses.includes(source.status));
});

const standardPopulations = computed(
  () => populationsStore.standardPopulations,
);
async function refreshPopulations() {
  modals.convertToCustom = false;
  await populationsStore.refreshPopulationsStore();
}

function handleDeletePopulation() {
  showDeleteModal.value = true;
}
function updateFilters(sourceFilterExpressions: Record<string, FilterGroup>) {
  const filters = Object.keys(sourceFilterExpressions);
  const newSourceFilter: Record<string, FilterGroup> = {};
  filters.forEach((filter) => {
    const currentFilter = sourceFilterExpressions[filter];
    let newExpression: string[] = [];
    let newParts: FilterPart[] = [];
    if (currentFilter?.filter_parts && currentFilter?.filter_expression) {
      newExpression = currentFilter.filter_expression.slice();
      newParts = currentFilter.filter_parts.map((part: FilterPart) => {
        const oldLabel = part.label;
        const oldLabelIndex = currentFilter.filter_expression.indexOf(oldLabel);
        const newPart = {
          ...part,
          label: uuidv4(),
        };
        // Handle optional id property
        if ('id' in newPart) {
          delete (newPart as FilterPart & { id?: unknown }).id;
        }
        newExpression[oldLabelIndex] = newPart.label;
        return newPart;
      });
    }
    newSourceFilter[filter] = {
      filter_expression: newExpression,
      filter_parts: newParts,
    };
  });
  return newSourceFilter;
}

function generateUniqueCopyName(baseName: string): string {
  const existingNames = new Set(
    populationsStore.populations.map((pop) => pop.name.toLowerCase()),
  );

  let copyNumber = 1;
  let candidateName = `${baseName} (Copy)`;

  while (existingNames.has(candidateName.toLowerCase())) {
    copyNumber++;
    candidateName = `${baseName} (Copy ${copyNumber})`;
  }

  return candidateName;
}

async function duplicatePopulation() {
  const name = generateUniqueCopyName(population.name);
  const sourceFilterExpressions = updateFilters(
    population.source_filter_expressions || {},
  );
  const duplicate = {
    name,
    source_id: population.source_id,
    source_filter_expressions: sourceFilterExpressions,
    segment: population.segment,
    standard_type: null,
  };
  const { error } = await crossbeamApi.POST('/v0.3/populations', {
    body: duplicate,
  });
  if (error) {
    flashesStore.addErrorFlash({
      message: 'Population Not Duplicated',
      description:
        'Please try again or contact support@crossbeam.com for help.',
    });
    return;
  }

  await populationsStore.refreshPopulationsStore();
  flashesStore.addSuccessFlash({ message: `${population.name} Duplicated` });

  setTimeout(async () => {
    await populationsStore.refreshPopulationsStore();
  }, 1000);
}

const isOpenData = computed(() => isOpenDataPopulation(population));
const isEmptyPopulation = computed(() => !!population?.isEmpty);
const isDisabled = computed(
  () => isEmptyPopulation.value || sourceDeleting.value,
);

const recordNumber = computed(() => population?.meta?.total_size);

const isUpdating = computed(
  () =>
    !isEmptyPopulation.value && !population.current_version?.first_processed_at,
);

const sharingLink = computed(() => {
  if (canManageSharing.value) {
    return {
      name: 'sharing-settings-by-population',
      params: { population_id: population.id },
    };
  }
  return {
    name: 'populations.sharing',
    params: { population_id: population.id },
  };
});

const pluralizedRecords = computed(() => {
  if (recordNumber.value === undefined) return 'calculating...';
  return `${intComma(recordNumber.value)}`;
});

const divOrTooltip = computed(() =>
  !population.standard_type ? BittsTooltip : 'div',
);

const showBorder = computed(
  () =>
    ![FILE_UPLOAD_DATA_SOURCE_TYPE, GOOGLE_SHEETS_DATA_SOURCE_TYPE].includes(
      population.integration_type,
    ),
);

// Offline partner delete functionality
async function handleOfflineDeletePopulation() {
  if (!isOfflinePartner || !offlinePartnerUuid) return;

  // Get partner ID from route params
  const partnerId = route.params.partner_org_id;

  await router.push({
    name: 'offline-details-delete-population-modal',
    params: { population_id: population.id },
    query: { offlinePartnerId: partnerId },
  });
}

function handlePopulationClick() {
  if (sourceDeleting.value) return;

  if (isOpenData.value) {
    emit('view-population');
    return;
  }

  if (isOfflinePartner && !canManageOfflinePartners.value) return;
  if (!isOfflinePartner && !canManagePopulations.value) return;
  emit('view-population');
}

async function onEditSharing() {
  await router.push({
    name: 'populations.sharing',
    params: { population_id: population.id },
    query: {
      ...route.query,
      toEdit: 'true',
    },
  });
}
</script>
<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-population-item {
  @apply flex w-full items-center justify-between p-16 cursor-default;
}
.c-population-item--new {
  @apply cursor-pointer;
}
.c-population-item__name {
  @apply mr-12 text-neutral-text-strong font-bold text-left;
}
.c-population-item--new .c-population-item__name {
  @apply cursor-pointer;
}
.c-population-item__open-data-icon {
  @apply mr-12 size-[32px] rounded-bts-sm border border-solid border-neutral-border
    bg-white flex items-center justify-center shrink-0;
}
:deep(.c-population-item__integration-avatar) {
  @apply mr-12;
  span {
    @apply bg-white!;
  }
}

:deep(.c-population-item__overflow-menu .bitts-overflow-menu) {
  @apply w-[240px];
}

:deep(.c-population-item__overflow-menu .population-overflow-menu__content) {
  @apply gap-4 p-4;
}
:deep(.c-population-item__overflow-menu .population-overflow-menu__item) {
  @apply text-base text-neutral-text-strong px-8 py-4;
  &:hover {
    @apply rounded-6;
  }
}
</style>
